import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../../../contexts/SettingsContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { SidebarSkeleton } from '../../../components/SkeletonLoader';
import Popover, { PopoverDivider, PopoverMenuItem } from '../../../components/Popover';
import { clearAuthSession, getMetadata } from '../../../utils/encryptedStorage';
import aiAutomationApi from '../../../utils/aiAutomationApi';
import NotificationSummaryList from '../../../components/notifications/NotificationSummaryList';
import { getNotificationGroupNavigationPath, getNotificationTotalCount } from '../../../utils/notificationUtils';
import '../../../styles/chat.css';

/** Relative time label: "1 min ago", "3 hrs ago", "Yesterday", "5 days ago", "Jun 12", "Jun 2021" */
const relativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return diffHr === 1 ? '1 hr ago' : `${diffHr} hrs ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} week${Math.floor(diffDay / 7) > 1 ? 's' : ''} ago`;

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const sameYear = date.getFullYear() === now.getFullYear();
  if (sameYear) return `${months[date.getMonth()]} ${date.getDate()}`;
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const getTitleInitial = (title) => {
  const value = (title || '').trim();
  if (!value) return 'J';
  return value.charAt(0).toUpperCase();
};

const getUserInfo = () => {
  try {
    const metadata = getMetadata();
    if (metadata?.name) {
      return {
        name: metadata.name || 'User',
        email: metadata.email || '',
        profile: metadata.profile || null,
        role: metadata.role || metadata.designation || 'HR',
      };
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      return {
        name: userData.name || 'User',
        email: userData.email || '',
        profile: userData.profile || null,
        role: userData.role || userData.designation || 'HR',
      };
    }
  } catch (e) {
    console.error('Error parsing user info:', e);
  }

  return { name: 'User', email: '', profile: null, role: 'HR' };
};

const ProfileSettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ProfileSignOutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const SidebarProfileMenu = ({
  isCollapsed = false,
  user,
  userInitial,
  profileSrc,
  primaryColor,
  onSettings,
  onLogout,
}) => {
  const avatar = (
    <>
      {profileSrc ? (
        <img
          src={profileSrc}
          alt={user.name}
          className={`${isCollapsed ? 'h-9 w-9' : 'h-8 w-8'} flex-shrink-0 rounded-full object-cover`}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div
        className={`${isCollapsed ? 'h-9 w-9' : 'h-8 w-8'} flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white`}
        style={{
          backgroundColor: primaryColor,
          display: profileSrc ? 'none' : 'flex',
        }}
      >
        {userInitial}
      </div>
    </>
  );

  return (
    <Popover
      position="top-left"
      className={isCollapsed ? 'leading-none' : 'block w-full'}
      contentClassName={isCollapsed ? 'w-auto min-w-0 p-1' : 'w-56 min-w-[200px]'}
      trigger={
        isCollapsed ? (
          <div
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full"
            title={user.name || 'User'}
            aria-label="Open profile menu"
          >
            {avatar}
          </div>
        ) : (
          <div className="flex w-full cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 transition-colors duration-250 hover:bg-gray-100 dark:hover:bg-gray-800">
            {avatar}
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-sm font-medium leading-5 text-gray-900 transition-colors duration-250 dark:text-white">
                {user.name || 'User'}
              </p>
              <p className="truncate text-xs leading-4 text-gray-500 transition-colors duration-250 dark:text-gray-400">
                {user.role || 'HR'}
              </p>
            </div>
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <ProfileSettingsIcon />
            </span>
          </div>
        )
      }
    >
      {({ close }) => (
        <>
          {!isCollapsed && (
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {user.name || 'User'}
              </p>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {user.email || ''}
              </p>
            </div>
          )}
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-700 transition-colors duration-250 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Settings"
                title="Settings"
                onClick={() => {
                  close();
                  onSettings?.();
                }}
              >
                <ProfileSettingsIcon />
              </button>
              <div className="h-px w-7 bg-gray-200 dark:bg-gray-700" />
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-red-600 transition-colors duration-250 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                aria-label="Logout"
                title="Logout"
                onClick={() => {
                  close();
                  onLogout?.();
                }}
              >
                <ProfileSignOutIcon />
              </button>
            </div>
          ) : (
            <div className="p-2">
              <PopoverMenuItem
                icon={<ProfileSettingsIcon />}
                onClick={() => {
                  close();
                  onSettings?.();
                }}
              >
                Settings
              </PopoverMenuItem>
              <PopoverDivider />
              <PopoverMenuItem
                icon={<ProfileSignOutIcon />}
                danger
                onClick={() => {
                  close();
                  onLogout?.();
                }}
              >
                Logout
              </PopoverMenuItem>
            </div>
          )}
        </>
      )}
    </Popover>
  );
};

/** Three-dot context menu for a sidebar job item */
const JobContextMenu = ({ posting, isOpen, onClose, onRename, onArchive, onDelete, onRestore, isArchived, menuRef }) => {
  if (!isOpen) return null;

  const canDelete = !(posting.candidateCount > 0 || posting.hasPostedJob);

  return (
    <div
      ref={menuRef}
      className="absolute right-2 top-8 z-50 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 text-sm"
      onClick={(e) => e.stopPropagation()}
    >
      {isArchived ? (
        <>
          <button
            className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
            onClick={() => { onRestore(posting.id); onClose(); }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Restore
          </button>
          {canDelete && (
            <button
              className="w-full text-left px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
              onClick={() => { onDelete(posting.id); onClose(); }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </button>
          )}
        </>
      ) : (
        <>
          <button
            className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
            onClick={() => { onRename(posting); onClose(); }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Rename
          </button>
          <button
            className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
            onClick={() => { onArchive(posting.id); onClose(); }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
            Archive
          </button>
          {canDelete && (
            <button
              className="w-full text-left px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
              onClick={() => { onDelete(posting.id); onClose(); }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </button>
          )}
        </>
      )}
    </div>
  );
};

/** Single job item row with three-dot menu */
const JobItem = ({ posting, isSelected, isDark, primaryColor, hexToRgba, onSelect, onRename, onArchive, onDelete, onRestore, isArchived }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Focus input on rename
  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  const handleRenameStart = (p) => {
    setRenameValue(p.title || '');
    setRenaming(true);
  };

  const handleRenameSave = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== posting.title) {
      onRename(posting.id, trimmed);
    }
    setRenaming(false);
  };

  const formatCompactTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const diffHr = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
    if (diffHr < 1) return 'now';
    if (diffHr < 24) return `${diffHr}h`;
    return `${Math.floor(diffHr / 24)}d`;
  };

  const formatShortDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Recently';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const timeLabel = formatCompactTime(posting.rawTimestamp) || relativeTime(posting.rawTimestamp);
  const dateLabel = formatShortDate(posting.rawTimestamp);
  const cardStyle = isArchived
    ? {
        opacity: 0.66,
        background: 'transparent',
        border: '1px solid transparent',
      }
    : isSelected
      ? {
          background: isDark
            ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.3)} 0%, ${hexToRgba('#28ACA8', 0.24)} 100%)`
            : `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.18)} 0%, ${hexToRgba('#28ACA8', 0.14)} 100%)`,
          border: `1px solid ${hexToRgba(primaryColor, 0.45)}`,
          boxShadow: isDark ? '0 10px 24px rgba(15,23,42,0.24)' : '0 12px 28px rgba(59,91,219,0.12)',
          color: isDark ? '#F8FAFC' : '#162033',
        }
      : {
        background: 'transparent',
        border: '1px solid transparent',
        };
  const hoverCardStyle = isDark
    ? {
        background: 'rgba(30, 41, 59, 0.62)',
        border: '1px solid rgba(71, 85, 105, 0.72)',
      }
    : {
        background: 'rgba(255, 255, 255, 0.82)',
        border: '1px solid rgba(203, 213, 225, 0.92)',
      };

  const titleColor = isSelected
    ? (isDark ? '#F8FAFC' : '#162033')
    : isArchived
      ? (isDark ? '#6B7280' : '#9CA3AF')
      : (isDark ? '#F8FAFC' : '#111827');

  const metaColor = isDark ? '#94A3B8' : '#64748B';

  const statusLabel = isArchived ? 'ARCHIVED' : posting.hasPostedJob ? 'SENT' : isSelected ? 'ACTIVE' : 'DRAFT';
  const statusPillStyle = isArchived
    ? {
        color: isDark ? '#CBD5E1' : '#64748B',
        background: isDark ? 'rgba(100,116,139,0.2)' : '#F1F5F9',
      }
    : posting.hasPostedJob
      ? {
          color: '#059669',
          background: isDark ? 'rgba(16,185,129,0.18)' : '#EAFBF1',
        }
      : isSelected
        ? {
          color: primaryColor,
          background: isDark ? 'rgba(59,91,219,0.18)' : '#EEF2FF',
        }
        : {
            color: '#F97316',
            background: isDark ? 'rgba(249,115,22,0.16)' : '#FFF7ED',
          };

  return (
    <div className="relative group">
      <button
        className="w-full text-left rounded-xl transition-all duration-250 hover:bg-white/70 dark:hover:bg-slate-800/70"
        style={{
          ...cardStyle,
          padding: '10px 10px',
        }}
        onMouseEnter={(e) => {
          if (isSelected || isArchived) return;
          e.currentTarget.style.background = hoverCardStyle.background;
          e.currentTarget.style.border = hoverCardStyle.border;
        }}
        onMouseLeave={(e) => {
          if (isSelected || isArchived) return;
          e.currentTarget.style.background = cardStyle.background;
          e.currentTarget.style.border = cardStyle.border;
        }}
        onClick={() => !renaming && !isArchived && onSelect?.(posting)}
      >
        {renaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameSave();
              if (e.key === 'Escape') setRenaming(false);
            }}
            onBlur={handleRenameSave}
            className="w-full bg-transparent border-b-2 border-blue-500 outline-none text-sm font-medium text-gray-800 dark:text-gray-200"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="flex items-center gap-3">
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 700,
                color: isSelected ? '#FFFFFF' : primaryColor,
                background: isSelected
                  ? `linear-gradient(135deg, ${primaryColor} 0%, #28ACA8 100%)`
                  : (isDark ? 'rgba(59,91,219,0.18)' : '#EEF2FF'),
                border: `1px solid ${isSelected ? 'transparent' : hexToRgba(primaryColor, isDark ? 0.18 : 0.08)}`,
                position: 'relative',
              }}
            >
              {getTitleInitial(posting.title)}
              {isSelected && (
                <span
                  style={{
                    position: 'absolute',
                    right: -1,
                    bottom: -1,
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: '#10B981',
                    border: `2px solid ${isDark ? '#1E293B' : '#FFFFFF'}`,
                  }}
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p
                  className="truncate"
                  style={{
                    color: titleColor,
                    fontSize: '14px',
                    lineHeight: '18px',
                    fontWeight: 700,
                  }}
                >
                  {posting.title}
                </p>
              </div>
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  color: metaColor,
                  fontSize: 11,
                  lineHeight: '14px',
                }}
              >
                <span className="inline-flex items-center gap-0.5">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 0 0-8 0v2" />
                    <circle cx="12" cy="7" r="4" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a3 3 0 0 0-2-2.83M4 21v-2a3 3 0 0 1 2-2.83" />
                  </svg>
                  {posting.candidateCount || 0}
                </span>
                <span aria-hidden="true">.</span>
                <span className="inline-flex items-center gap-0.5">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="12" cy="12" r="9" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
                  </svg>
                  {dateLabel}
                </span>
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-col items-end gap-2">
              <span
                className="transition-opacity duration-150 group-hover:opacity-0"
                style={{ color: metaColor, fontSize: 12, lineHeight: '14px' }}
              >
                {timeLabel}
              </span>
              {isArchived && (
                <span
                  style={{
                    padding: '3px 7px',
                    borderRadius: 6,
                    fontSize: '10px',
                    lineHeight: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    ...statusPillStyle,
                  }}
                >
                  {statusLabel}
                </span>
              )}
            </div>
          </div>
        )}
      </button>

      {/* Three-dot menu trigger */}
      <button
        className="absolute right-1 top-1 rounded-lg p-1 text-gray-500 opacity-0 transition-opacity hover:bg-gray-300/50 group-hover:opacity-100 dark:text-gray-400 dark:hover:bg-gray-600/50"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        title="More options"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      <JobContextMenu
        posting={posting}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onRename={handleRenameStart}
        onArchive={onArchive}
        onDelete={onDelete}
        onRestore={onRestore}
        isArchived={isArchived}
        menuRef={menuRef}
      />
    </div>
  );
};

const ChatSidebar = ({
  isCollapsed,
  isCompactLayout = false,
  isMobileLayout = false,
  onToggleCollapse,
  onRequestClose,
  appName = 'Wizbox',
  appLogo,
  jobPostings = [],
  selectedJobId,
  onSelectJob,
  onNewJobPosting,
  newChatCreating = false,
  loading = false,
  // Delete/Archive/Restore handlers
  onArchiveJob,
  onDeleteJob,
  onRenameJob,
  onRestoreJob,
  // Archived jobs
  archivedJobs = [],
  showArchived = false,
  onToggleArchived,
  archivedCount = 0,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const { isDark, toggleTheme } = useTheme();
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const chatSearchInputRef = useRef(null);

  useEffect(() => {
    if (!chatSearchOpen) return;
    const focusTimer = window.setTimeout(() => {
      chatSearchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [chatSearchOpen]);

  const fetchNotifications = async () => {
    try {
      setNotifLoading(true);
      const metadata = getMetadata();
      const cid = metadata?.cid || '';
      const data = await aiAutomationApi.get(`/interviews/action-notifications${cid ? `?cid=${cid}` : ''}`);
      const nextNotifications = Array.isArray(data?.notifications) ? data.notifications : [];
      setNotifications(nextNotifications);
      setNotifCount(getNotificationTotalCount(data, nextNotifications));
    } catch (err) {
      console.error('Failed to fetch sidebar notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const handleNotifChanged = () => fetchNotifications();
    window.addEventListener('notificationsChanged', handleNotifChanged);
    return () => window.removeEventListener('notificationsChanged', handleNotifChanged);
  }, []);

  const handleClearAllNotifications = async () => {
    try {
      const metadata = getMetadata();
      const cid = metadata?.cid || '';
      await aiAutomationApi.patch(`/interviews/notifications/clear-all${cid ? `?cid=${cid}` : ''}`);
      setNotifications([]);
      setNotifCount(0);
    } catch (err) {
      console.error('Failed to clear sidebar notifications:', err);
    }
  };

  const handleNotificationGroupOpen = (group, close) => {
    navigate(getNotificationGroupNavigationPath(group));
    close?.();
  };

  const tealAccent = '#28ACA8';
  const sidebarBg = isDark ? '#0B1220' : '#F1F5FB';
  const user = getUserInfo();
  const userInitial = user.name ? user.name.charAt(0).toUpperCase() : 'U';
  const profileSrc = user.profile
    ? (/^(https?:)?\/\//.test(user.profile) || user.profile.startsWith('data:')
        ? user.profile
        : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${user.profile}`)
    : '';

  const hexToRgba = (hex, alpha) => {
    const safe = (hex || '').replace('#', '');
    if (safe.length !== 6) return hex;
    const r = parseInt(safe.slice(0, 2), 16);
    const g = parseInt(safe.slice(2, 4), 16);
    const b = parseInt(safe.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const PencilIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 5H6C4.89543 5 4 5.89543 4 7V18C4 19.1046 4.89543 20 6 20H17C18.1046 20 19 19.1046 19 18V13M17.5858 3.58579C18.3668 2.80474 19.6332 2.80474 20.4142 3.58579C21.1953 4.36683 21.1953 5.63316 20.4142 6.41421L11.8284 15H9L9 12.1716L17.5858 3.58579Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const NewChatIcon = () => (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7.5 18.5L4 21V6.5A3.5 3.5 0 0 1 7.5 3h9A3.5 3.5 0 0 1 20 6.5v7A3.5 3.5 0 0 1 16.5 17H9.2L7.5 18.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 7.5v6M9 10.5h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
    </svg>
  );

  const ArrowLeftIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.0001 17C12.8685 17.0008 12.738 16.9756 12.6162 16.9258C12.4943 16.876 12.3835 16.8027 12.2901 16.71L8.29006 12.71C8.10381 12.5227 7.99927 12.2692 7.99927 12.005C7.99927 11.7408 8.10381 11.4874 8.29006 11.3L12.2901 7.30003C12.4814 7.1362 12.7274 7.05059 12.9791 7.06032C13.2308 7.07004 13.4695 7.17437 13.6476 7.35246C13.8257 7.53056 13.9301 7.7693 13.9398 8.02097C13.9495 8.27265 13.8639 8.51873 13.7001 8.71003L10.4101 12L13.7001 15.29C13.8405 15.4293 13.9365 15.6071 13.9758 15.8009C14.0152 15.9947 13.9963 16.1958 13.9213 16.3789C13.8464 16.5619 13.7189 16.7186 13.5549 16.8292C13.3909 16.9397 13.1978 16.9992 13.0001 17Z" fill="currentColor"/>
      <path d="M19 13H9C8.73478 13 8.48043 12.8946 8.29289 12.7071C8.10536 12.5196 8 12.2652 8 12C8 11.7348 8.10536 11.4804 8.29289 11.2929C8.48043 11.1054 8.73478 11 9 11H19C19.2652 11 19.5196 11.1054 19.7071 11.2929C19.8946 11.4804 20 11.7348 20 12C20 12.2652 19.8946 12.5196 19.7071 12.7071C19.5196 12.8946 19.2652 13 19 13ZM5 20C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V5C4 4.73478 4.10536 4.48043 4.29289 4.29289C4.48043 4.10536 4.73478 4 5 4C5.26522 4 5.51957 4.10536 5.70711 4.29289C5.89464 4.48043 6 4.73478 6 5V19C6 19.2652 5.89464 19.5196 5.70711 19.7071C5.51957 19.8946 5.26522 20 5 20Z" fill="currentColor"/>
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
      <path d="M13.0001 17C12.8685 17.0008 12.738 16.9756 12.6162 16.9258C12.4943 16.876 12.3835 16.8027 12.2901 16.71L8.29006 12.71C8.10381 12.5227 7.99927 12.2692 7.99927 12.005C7.99927 11.7408 8.10381 11.4874 8.29006 11.3L12.2901 7.30003C12.4814 7.1362 12.7274 7.05059 12.9791 7.06032C13.2308 7.07004 13.4695 7.17437 13.6476 7.35246C13.8257 7.53056 13.9301 7.7693 13.9398 8.02097C13.9495 8.27265 13.8639 8.51873 13.7001 8.71003L10.4101 12L13.7001 15.29C13.8405 15.4293 13.9365 15.6071 13.9758 15.8009C14.0152 15.9947 13.9963 16.1958 13.9213 16.3789C13.8464 16.5619 13.7189 16.7186 13.5549 16.8292C13.3909 16.9397 13.1978 16.9992 13.0001 17Z" fill="currentColor"/>
      <path d="M19 13H9C8.73478 13 8.48043 12.8946 8.29289 12.7071C8.10536 12.5196 8 12.2652 8 12C8 11.7348 8.10536 11.4804 8.29289 11.2929C8.48043 11.1054 8.73478 11 9 11H19C19.2652 11 19.5196 11.1054 19.7071 11.2929C19.8946 11.4804 20 11.7348 20 12C20 12.2652 19.8946 12.5196 19.7071 12.7071C19.5196 12.8946 19.2652 13 19 13ZM5 20C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V5C4 4.73478 4.10536 4.48043 4.29289 4.29289C4.48043 4.10536 4.73478 4 5 4C5.26522 4 5.51957 4.10536 5.70711 4.29289C5.89464 4.48043 6 4.73478 6 5V19C6 19.2652 5.89464 19.5196 5.70711 19.7071C5.51957 19.8946 5.26522 20 5 20Z" fill="currentColor"/>
    </svg>
  );

  const BellIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );

  const SunIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );

  const MoonIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.99 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 20.99 12.79Z" />
    </svg>
  );

  const handleSignOut = () => {
    clearAuthSession();
    navigate('/auth/login');
  };

  const handleSettings = () => {
    const returnTo = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    navigate('/account/settings', { state: { returnTo } });
    closeAfterMobileAction();
  };

  const closeAfterMobileAction = () => {
    if (isMobileLayout) onRequestClose?.();
  };

  const navItems = [
    {
      label: 'Dashboard',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 10.5V4h6.5v6.5H4zm9.5 0V4H20v6.5h-6.5zM4 20v-6.5h6.5V20H4zm9.5 0v-6.5H20V20h-6.5z" />
        </svg>
      ),
      onClick: () => navigate('/dashboard'),
      active: location.pathname === '/dashboard',
    },
    {
      label: 'Campaigns',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
      onClick: () => navigate(`/chat?new=true&t=${Date.now()}`),
      active: location.pathname === '/chat',
    },
    {
      label: 'Master Database',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      onClick: () => navigate('/master-database'),
      active: location.pathname === '/master-database',
    },
    {
      label: 'Resume Database',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => navigate('/resume-parsing'),
      active: location.pathname === '/resume-parsing',
    },
  ];

  if (isCollapsed) {
    return (
      <aside
        className="h-full min-h-0 flex-shrink-0 overflow-visible border-r border-gray-200 dark:border-gray-700 flex flex-col items-center py-4 transition-all duration-250"
        style={{ backgroundColor: sidebarBg, width: isCompactLayout ? '56px' : '64px' }}
      >
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250"
          aria-label="Expand sidebar"
        >
          <ArrowRightIcon />
        </button>
        <button
          onClick={() => onNewJobPosting?.()}
          className="p-2 mt-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 text-gray-600 dark:text-gray-300"
          aria-label="New Job Posting"
          title="New Job Posting"
        >
          <PencilIcon />
        </button>
        <div className="mt-auto">
          <SidebarProfileMenu
            isCollapsed
            user={user}
            userInitial={userInitial}
            profileSrc={profileSrc}
            primaryColor={primaryColor}
            onSettings={handleSettings}
            onLogout={() => {
              handleSignOut();
              closeAfterMobileAction();
            }}
          />
        </div>
      </aside>
    );
  }

  const chats = jobPostings;
  const normalizedChatSearchQuery = chatSearchQuery.trim().toLowerCase();
  const visibleChats = normalizedChatSearchQuery
    ? chats.filter((posting) => String(posting.title || '').toLowerCase().includes(normalizedChatSearchQuery))
    : chats;
  const currentJob = selectedJobId ? jobPostings.find((p) => p.id === selectedJobId) : null;

  return (
    <aside
      className="h-full min-h-0 flex-shrink-0 overflow-hidden border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-250"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, #0B1220 0%, #111827 100%)'
          : 'linear-gradient(180deg, #F7FAFF 0%, #EEF4FB 100%)',
        width: isMobileLayout ? '100%' : isCompactLayout ? '276px' : '296px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 pb-2 pt-2 border-b border-gray-200 dark:border-gray-700"
        style={{
          background: isDark
            ? 'rgba(15, 23, 42, 0.72)'
            : `linear-gradient(180deg, ${hexToRgba(primaryColor, 0.06)} 0%, rgba(255,255,255,0.72) 100%)`,
          backdropFilter: 'blur(14px)',
        }}
      >
          <div className="flex items-center justify-between gap-3 mb-3">
            <button
              type="button"
              onClick={() => {
                navigate('/dashboard');
                closeAfterMobileAction();
              }}
            className="flex min-w-0 items-center gap-3 rounded-xl text-left transition-colors duration-250 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Go to dashboard"
            aria-label="Go to dashboard"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl">
              {appLogo ? (
                <img
                  src={appLogo}
                  alt={appName}
                  className="h-8 w-8 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement.textContent = (appName || 'W').charAt(0).toUpperCase();
                  }}
                />
              ) : (
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {(appName || 'W').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                {appName}
              </h2>
            </div>
          </button>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 text-gray-500 dark:text-gray-400"
              aria-label="Toggle theme"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>
            <Popover
              position="bottom-right"
              portal
              contentClassName="w-[320px] overflow-hidden"
              onOpen={fetchNotifications}
              trigger={
                <button
                  type="button"
                  className="relative flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 text-gray-500 dark:text-gray-400"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <BellIcon />
                  {notifCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </button>
              }
            >
              {({ close }) => (
                <div className="flex flex-col" style={{ maxHeight: 'min(460px, calc(100vh - 5rem))' }}>
                  <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      {notifCount > 0 && (
                        <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: primaryColor }}>
                          {notifCount}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      {notifications.length > 0 && (
                        <>
                          {notifCount > 3 && (
                            <button
                              type="button"
                              onClick={() => { close(); navigate('/notifications'); }}
                              className="text-[11px] font-medium text-blue-500 transition-colors hover:text-blue-700 dark:text-blue-400"
                            >
                              View All
                            </button>
                          )}
                        <button
                          type="button"
                          onClick={() => { handleClearAllNotifications(); close(); }}
                          className="text-[11px] font-medium text-gray-400 transition-colors hover:text-red-500"
                        >
                          Clear
                        </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={close}
                        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                        aria-label="Close notifications"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <NotificationSummaryList
                      notifications={notifications}
                      loading={notifLoading}
                      totalCount={notifCount}
                      primaryColor={primaryColor}
                      onOpenGroup={(group) => handleNotificationGroupOpen(group, close)}
                      onViewAll={() => { close(); navigate('/notifications'); }}
                    />
                  </div>
                  {notifCount > 0 && (
                    <div className="flex-shrink-0 border-t border-gray-100 px-4 py-2.5 text-center dark:border-gray-700">
                      <button
                        type="button"
                        onClick={() => { close(); navigate('/notifications'); }}
                        className="text-xs font-medium hover:underline"
                        style={{ color: primaryColor }}
                      >
                        View All Notifications →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Popover>
            <button
              onClick={onToggleCollapse}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 text-gray-500 dark:text-gray-400"
              aria-label="Collapse sidebar"
              title="Close sidebar"
            >
              <ArrowLeftIcon />
            </button>
          </div>
        </div>

        <div className="mb-2 space-y-0">
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                item.onClick();
                closeAfterMobileAction();
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-1.5 text-left transition-colors duration-250"
              style={{
                background: item.active
                  ? (isDark ? 'rgba(59, 91, 219, 0.18)' : 'rgba(59, 91, 219, 0.12)')
                  : 'transparent',
                color: item.active ? primaryColor : (isDark ? '#CBD5E1' : '#334155'),
              }}
            >
              <span
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                style={{
                  background: item.active
                    ? (isDark ? 'rgba(59, 91, 219, 0.24)' : 'rgba(59, 91, 219, 0.14)')
                    : (isDark ? 'rgba(15, 23, 42, 0.5)' : '#F8FAFC'),
                  border: `1px solid ${item.active ? 'transparent' : (isDark ? '#243041' : '#E2E8F0')}`,
                }}
              >
                {item.icon}
              </span>
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="-mx-4 mt-2 border-t border-gray-200 px-4 pt-2 dark:border-gray-700">
          {chatSearchOpen ? (
            <div
              className="flex h-10 items-center gap-2 rounded-xl px-3"
              style={{
                background: isDark ? 'rgba(15, 23, 42, 0.78)' : '#FFFFFF',
                border: `1px solid ${isDark ? '#334155' : '#DDE6F6'}`,
                boxShadow: isDark ? 'none' : '0 2px 8px rgba(59, 91, 219, 0.10)',
              }}
            >
              <svg className="flex-shrink-0 text-gray-500 dark:text-gray-400" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="11" cy="11" r="7" />
                <path strokeLinecap="round" d="m20 20-3.5-3.5" />
              </svg>
              <input
                ref={chatSearchInputRef}
                type="text"
                value={chatSearchQuery}
                onChange={(e) => setChatSearchQuery(e.target.value)}
                className="chat-sidebar-search-input min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500 focus:border-0 focus:outline-none focus:ring-0 dark:text-gray-100 dark:placeholder:text-gray-400"
                style={{ boxShadow: 'none' }}
                placeholder="Search chats..."
              />
              <button
                type="button"
                onClick={() => {
                  setChatSearchQuery('');
                  setChatSearchOpen(false);
                }}
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label="Close chat search"
                title="Close search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <h3 className="chat_text_sidebar_label uppercase" style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '0.04em', color: isDark ? '#CBD5E1' : '#334155' }}>
                  Chats
                </h3>
                <span
                  style={{
                    minWidth: 24,
                    height: 20,
                    padding: '0 8px',
                    borderRadius: 999,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: isDark ? '#CBD5E1' : '#64748B',
                    background: isDark ? 'rgba(15,23,42,0.62)' : '#FFFFFF',
                    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
                  }}
                >
                  {chats.length}
                </span>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setChatSearchOpen(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label="Search chats"
                  title="Search chats"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="11" cy="11" r="7" />
                    <path strokeLinecap="round" d="m20 20-3.5-3.5" />
                  </svg>
                </button>
                <span className="h-6 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => {
                    if (newChatCreating) return;
                    onNewJobPosting?.();
                  }}
                  disabled={newChatCreating}
                  className="flex h-7 items-center gap-1.5 rounded-full pl-1.5 pr-2.5 text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-75"
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${tealAccent} 100%)` }}
                  aria-label="New chat"
                  aria-busy={newChatCreating}
                  title={newChatCreating ? 'Opening new chat...' : 'New chat'}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                    {newChatCreating ? (
                      <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.35" />
                        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                        <path strokeLinecap="round" d="M12 5v14M5 12h14" />
                      </svg>
                    )}
                  </span>
                  <span className="text-[11px] font-bold leading-none">
                    {newChatCreating ? 'Opening...' : 'New chat'}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Job List — scrollable, leaves room for sticky archived toggle */}
      <div
        className="px-3 pt-1 pb-4 overflow-y-auto flex-1 min-h-0"
      >
        {loading ? (
          <SidebarSkeleton />
        ) : (
          <>
            {/* Current Chat */}
            {false && currentJob && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2 px-1">
                  <h3 className="chat_text_sidebar_label uppercase tracking-wide" style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', color: isDark ? '#94A3B8' : '#7A8597' }}>
                  Current Chat
                  </h3>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: '10px',
                      fontWeight: 700,
                      color: primaryColor,
                      background: hexToRgba(primaryColor, isDark ? 0.18 : 0.1),
                    }}
                  >
                    1 Active
                  </span>
                </div>
                <JobItem
                  posting={currentJob}
                  isSelected={true}
                  isDark={isDark}
                  primaryColor={primaryColor}
                  hexToRgba={hexToRgba}
                  onSelect={onSelectJob}
                  onRename={onRenameJob}
                  onArchive={onArchiveJob}
                  onDelete={onDeleteJob}
                />
              </div>
            )}

            {/* Chats */}
            {chats.length > 0 && (
              <div>
                {visibleChats.length > 0 ? (
                <div className="space-y-1">
                  {visibleChats.map((posting) => (
                    <JobItem
                      key={posting.id}
                      posting={posting}
                      isSelected={posting.id === selectedJobId}
                      isDark={isDark}
                      primaryColor={primaryColor}
                      hexToRgba={hexToRgba}
                      onSelect={onSelectJob}
                      onRename={onRenameJob}
                      onArchive={onArchiveJob}
                      onDelete={onDeleteJob}
                    />
                  ))}
                </div>
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No chats found
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {/* Archived toggle — sticky at bottom */}
      {(archivedCount > 0 || archivedJobs.length > 0) && (
        <div
          className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700"
          style={{ background: isDark ? 'rgba(15, 23, 42, 0.78)' : 'rgba(255,255,255,0.82)' }}
        >
          <button
            onClick={onToggleArchived}
            className="w-full flex items-center gap-2 px-5 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${showArchived ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span style={{ flex: 1, textAlign: 'left' }}>Archived</span>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: '10px',
                fontWeight: 700,
                color: isDark ? '#CBD5E1' : '#637086',
                background: isDark ? 'rgba(255,255,255,0.08)' : '#EEF2F7',
              }}
            >
              {archivedCount || archivedJobs.length}
            </span>
          </button>

          {showArchived && archivedJobs.length > 0 && (
            <div className="px-4 pb-3 space-y-2 max-h-48 overflow-y-auto">
              {archivedJobs.map((posting) => (
                <JobItem
                  key={posting.id}
                  posting={posting}
                  isSelected={false}
                  isDark={isDark}
                  primaryColor={primaryColor}
                  hexToRgba={hexToRgba}
                  onSelect={() => {}}
                  onRename={() => {}}
                  onArchive={() => {}}
                  onDelete={onDeleteJob}
                  onRestore={onRestoreJob}
                  isArchived={true}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div
        className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 py-2"
        style={{ background: isDark ? 'rgba(15, 23, 42, 0.86)' : 'rgba(255,255,255,0.9)' }}
      >
        <SidebarProfileMenu
          user={user}
          userInitial={userInitial}
          profileSrc={profileSrc}
          primaryColor={primaryColor}
          onSettings={handleSettings}
          onLogout={() => {
            handleSignOut();
            closeAfterMobileAction();
          }}
        />
      </div>
    </aside>
  );
};

export default ChatSidebar;
